// Forward kinematics on a body clip's bone hierarchy, reduced to ONE rigid
// transform: where the head bone has moved since its rest pose.
//
// The hair is rigid relative to the head, so it needs no skinning and no vertex
// correspondence — the head's own delta, applied to every hair vertex, is the
// whole drive. THREE does the FK: a mixer on the clip's hierarchy, then read the
// head bone's world matrix.
//
// FRAME. The clip GLB carries the artist's FBX frame (centimetres, a permuted
// axis convention) while the trained branch is metres in the export's frame, so
// the delta is CONJUGATED into the branch frame: D_branch = C · D_glb · C⁻¹,
// with C = partToBranchMatrix. Conjugation, not a one-sided multiply — a delta
// is a change of pose, not a point, so both sides have to change frame or the
// rotation axis comes out wrong while the translation looks plausible.

import * as THREE from 'three'
import { partToBranchMatrix } from '../inference/partBranchVertices.js'

/** The bone the hair rides. */
export const HEAD_BONE = 'head'

// The GLB↔branch change of basis, built once: a delta is conjugated by it, never
// multiplied on one side (see the FRAME note above).
const TO_BRANCH = partToBranchMatrix()
const TO_GLB = TO_BRANCH.clone().invert()

/** The head bone plus its REST world inverse, captured before any mixer poses `root`.
 *
 *  Rest first is not a nicety: the delta is measured against the pose the hair's trained
 *  neutral belongs to, and a mixer sitting at t=0 already holds the clip's first frame. */
export function captureHeadRest(root, context = '') {
  const boneName = HEAD_BONE
  let head = null
  let skinned = null
  root.traverse((n) => {
    if (!head && n.name === boneName) head = n
    if (!skinned && n.isSkinnedMesh) skinned = n
  })
  if (!head) {
    const names = []
    root.traverse((n) => { if (n.name && names.length < 12) names.push(n.name) })
    throw new Error(`${context}no '${boneName}' node (saw ${names.join(', ')}…)`)
  }
  root.updateMatrixWorld(true)
  return {
    bone: head,
    inverse: head.matrixWorld.clone().invert(),
    // The part's bind inverse, because a part's solved fit maps its SKINNED vertex space —
    // which is post-bindMatrixInverse — and the bone's matrixWorld is not in that space.
    // The two together are the whole map from bone world to branch (see headRigidDelta).
    bindInverse: skinned?.bindMatrixInverse ? skinned.bindMatrixInverse.clone() : null,
  }
}

/** The change of basis for ONE part: its solved signed axis permutation and cm->m scale,
 *  WITH its translation. The shared TO_BRANCH above has none, and for a conjugation the
 *  translation is the origin the rotation happens about. */
function partToBranch(fit, out = new THREE.Matrix4()) {
  const { axes, signs } = fit.permutation
  const t = fit.translation
  const e = out.elements
  e.fill(0)
  for (let row = 0; row < 3; row++) e[axes[row] * 4 + row] = signs[row] * 0.01
  e[12] = t[0]; e[13] = t[1]; e[14] = t[2]; e[15] = 1
  return out
}

const PART_C = new THREE.Matrix4()
const PART_C_INV = new THREE.Matrix4()

/**
 * Where the head has moved since rest, in the BRANCH frame. Caller poses `root` first.
 *
 * `frame` is the solved fit of the PART the bone was read from. Pass it: the delta is a
 * change of POSE, so it is conjugated, and a conjugation is only correct in the basis the
 * points are actually expressed in. The shared TO_BRANCH is that basis apart from its
 * TRANSLATION, which it does not carry — and dropping the translation from a conjugation
 * leaves an error of (I - R)·offset. That is exactly zero while the head is unrotated and
 * grows with the angle, so it reads as the head swinging away from the neck the further it
 * turns rather than as a constant misplacement. Measured on myragaussainsplatting: the face
 * part sits 5.5 cm off, the head's delta rotates a median 51 degrees over a walk, and the
 * head was displaced by a median of 10.5 mm (max 16.2).
 *
 * Without `frame` it falls back to the translation-free shared matrix — correct only for a
 * part whose own offset is zero, and kept so a caller that has no fits still gets the
 * rotation right.
 */
export function headRigidDelta(rest, out = new THREE.Matrix4(), frame = null) {
  out.multiplyMatrices(rest.bone.matrixWorld, rest.inverse)
  if (!frame?.permutation || !frame?.translation) return out.premultiply(TO_BRANCH).multiply(TO_GLB)
  // The WHOLE map from the bone's world space to branch space: the part's bind inverse
  // first (the fit is defined on skinned vertices, which are post-bindMatrixInverse), then
  // the fit. Conjugating by the fit alone leaves the delta's translation wrong by the bind
  // matrix's own, and a rigid transform whose translation is wrong is a rotation about the
  // wrong POINT: measured on myragaussainsplatting the head then pivoted about y = -0.003,
  // the floor, instead of about its joint at y = -1.323 — a hinge 1.3 m below the neck,
  // which is what "the head rotates from too low a point and swings off the body" is.
  partToBranch(frame, PART_C)
  if (rest.bindInverse) PART_C.multiply(rest.bindInverse)
  PART_C_INV.copy(PART_C).invert()
  return out.premultiply(PART_C).multiply(PART_C_INV)
}
